/**
 * Cookie Session Monitor Hook
 *
 * Actively monitors authentication cookies and automatically logs out
 * when cookies expire or are deleted. This provides automatic session
 * cleanup when cookies reach their max-age.
 */

import { useEffect } from "react";
import SecureStorageService from "../utils/secure-storage";

interface UseCookieSessionMonitorProps {
  /** Auth token key to monitor */
  authTokenKey: string;
  /** Callback when session expires */
  onSessionExpired: () => void;
  /** Check interval in milliseconds (default: 10 seconds) */
  checkInterval?: number;
  /** Whether monitoring is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Hook to monitor cookie-based authentication sessions
 *
 * @example
 * ```tsx
 * useCookieSessionMonitor({
 *   authTokenKey: 'adora-auth-token',
 *   onSessionExpired: () => Auth.logout(true),
 *   checkInterval: 10000 // Check every 10 seconds
 * });
 * ```
 */
export const useCookieSessionMonitor = ({
  authTokenKey,
  onSessionExpired,
  checkInterval = 10_000, // 10 seconds
  enabled = true
}: UseCookieSessionMonitorProps) => {
  useEffect(() => {
    // If monitoring is disabled, don't start
    if (!enabled) {
      return;
    }

    // Check if token exists when starting monitoring
    const initialToken = SecureStorageService.getItem(authTokenKey);
    if (!initialToken) {
      // No token when trying to start monitoring
      // User is not logged in or just logged out
      return;
    }

    // Set up periodic checks for cookie expiration
    const intervalId = setInterval(() => {
      const token = SecureStorageService.getItem(authTokenKey);

      if (!token) {
        // Token has expired or been deleted - trigger logout
        clearInterval(intervalId);
        onSessionExpired();
      }
    }, checkInterval);

    // Cleanup: stop monitoring when component unmounts or dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [authTokenKey, onSessionExpired, checkInterval, enabled]);
};

export default useCookieSessionMonitor;
